import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./FoodRadarOffers.css";

const OFFER_FILTERS = [
  { key: "all", label: "All Offers", icon: "🎁" },
  { key: "combo", label: "Combos", icon: "🍔" },
  { key: "bill_discount", label: "Discounts", icon: "🏷️" },
  { key: "bill_unlock_addon", label: "Add-on", icon: "🔓" },
  { key: "buy_x_get_y", label: "Buy X Get Y", icon: "🎉" },
  { key: "time_slot", label: "Happy Hour", icon: "⏰" },
  { key: "progressive_unlock", label: "Unlock Deals", icon: "🚀" },
  { key: "coin_reward", label: "Coins", icon: "🪙" },
];

const SORT_OPTIONS = [
  { value: "nearest", label: "Nearest First" },
  { value: "most_offers", label: "Most Offers" },
  { value: "highest_saving", label: "Highest Saving" },
  { value: "ending_soon", label: "Ending Soon" },
];

const safeNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const safeJsonParse = (value, fallback = null) => {
  try {
    if (value === null || value === undefined || value === "") {
      return fallback;
    }

    if (typeof value === "object") {
      return value;
    }

    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
};

const normalizeOfferType = (value) => {
  const type = String(value || "")
    .trim()
    .toLowerCase();

  if (["combo", "combo_set", "combo_offer"].includes(type)) {
    return "combo";
  }

  if (
    ["bill_unlock_addon", "addon", "unlock_addon", "bill_addon"].includes(type)
  ) {
    return "bill_unlock_addon";
  }

  if (
    ["buy_x_get_y", "buyxgety", "bogo", "buy_get", "buy_x_get"].includes(type)
  ) {
    return "buy_x_get_y";
  }

  if (
    [
      "time_slot",
      "time_slot_offer",
      "time_based",
      "happy_hour",
      "happy_hours",
    ].includes(type)
  ) {
    return "time_slot";
  }

  if (
    [
      "progressive_unlock",
      "progressive",
      "tier_unlock",
      "multi_unlock",
    ].includes(type)
  ) {
    return "progressive_unlock";
  }

  if (
    [
      "coin_reward",
      "coins_reward",
      "shop_coins",
      "loyalty_coins",
      "coin",
      "coins",
    ].includes(type)
  ) {
    return "coin_reward";
  }

  return "bill_discount";
};

const getOfferTypeConfig = (offerType) => {
  const type = normalizeOfferType(offerType);

  const configs = {
    combo: {
      label: "Combo Deal",
      icon: "🍔",
      className: "combo",
    },
    bill_discount: {
      label: "Bill Discount",
      icon: "🏷️",
      className: "discount",
    },
    bill_unlock_addon: {
      label: "Bill Unlock Deal",
      icon: "🔓",
      className: "addon",
    },
    buy_x_get_y: {
      label: "Buy X Get Y",
      icon: "🎉",
      className: "buy-get",
    },
    time_slot: {
      label: "Happy Hour",
      icon: "⏰",
      className: "time",
    },
    progressive_unlock: {
      label: "Progressive Unlock",
      icon: "🚀",
      className: "progressive",
    },
    coin_reward: {
      label: "Shop Coins",
      icon: "🪙",
      className: "coins",
    },
  };

  return configs[type] || configs.bill_discount;
};

const formatMoney = (value) => {
  const amount = safeNumber(value, 0);

  return `₹${amount.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;
};

const formatTime = (value) => {
  if (!value) {
    return "";
  }

  const text = String(value).trim();
  const match = text.match(/^(\d{1,2}):(\d{2})/);

  if (!match) {
    return text;
  }

  const hour24 = Number(match[1]);
  const minute = match[2];

  if (!Number.isFinite(hour24)) {
    return text;
  }

  const period = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 || 12;

  return `${hour12}:${minute} ${period}`;
};

const getVendorImageSrc = (vendor) => {
  const rawValue =
    vendor?.shopImageUrl ||
    vendor?.shop_image_url ||
    vendor?.shopLogoUrl ||
    vendor?.shop_logo_url ||
    vendor?.shopImage ||
    vendor?.shop_image ||
    vendor?.shopLogo ||
    vendor?.shop_logo ||
    vendor?.image ||
    "";

  if (!rawValue) {
    return "";
  }

  let imageValue = rawValue;

  if (typeof imageValue !== "string") {
    if (Array.isArray(imageValue)) {
      imageValue = imageValue[0] || "";
    } else if (typeof imageValue === "object") {
      imageValue =
        imageValue.url ||
        imageValue.path ||
        imageValue.image ||
        imageValue.filename ||
        "";
    }
  }

  imageValue = String(imageValue || "").trim();

  const parsed = safeJsonParse(imageValue, null);

  if (Array.isArray(parsed)) {
    imageValue = String(parsed[0] || "").trim();
  } else if (typeof parsed === "string") {
    imageValue = parsed.trim();
  } else if (parsed && typeof parsed === "object") {
    imageValue = String(
      parsed.url ||
        parsed.path ||
        parsed.image ||
        parsed.filename ||
        parsed.file ||
        ""
    ).trim();
  }

  if (!imageValue) {
    return "";
  }

  if (
    imageValue.startsWith("http://") ||
    imageValue.startsWith("https://") ||
    imageValue.startsWith("data:") ||
    imageValue.startsWith("blob:")
  ) {
    return imageValue;
  }

  if (imageValue.startsWith("/")) {
    return imageValue;
  }

  if (imageValue.startsWith("uploads/")) {
    return `/${imageValue}`;
  }

  return `/uploads/${imageValue}`;
};

const normalizeOffer = (rawOffer = {}) => {
  const offerJson =
    safeJsonParse(rawOffer.offer_json, {}) ||
    safeJsonParse(rawOffer.extra_json, {}) ||
    safeJsonParse(rawOffer.meta_json, {}) ||
    {};

  const source = {
    ...offerJson,
    ...rawOffer,
  };

  const offerType = normalizeOfferType(
    source.offer_type || source.offerType || source.type
  );

  let comboItems =
    source.combo_items ||
    source.comboItems ||
    offerJson.combo_items ||
    offerJson.comboItems ||
    [];

  comboItems = safeJsonParse(comboItems, comboItems);

  if (!Array.isArray(comboItems)) {
    comboItems = [];
  }

  let progressiveSteps =
    source.progressive_steps ||
    source.progressiveSteps ||
    offerJson.progressive_steps ||
    offerJson.progressiveSteps ||
    [];

  progressiveSteps = safeJsonParse(progressiveSteps, progressiveSteps);

  if (!Array.isArray(progressiveSteps)) {
    progressiveSteps = [];
  }

  let daysActive =
    source.days_active ||
    source.daysActive ||
    offerJson.days_active ||
    offerJson.daysActive ||
    [];

  daysActive = safeJsonParse(daysActive, daysActive);

  if (typeof daysActive === "string") {
    daysActive = daysActive
      .split(",")
      .map((day) => day.trim())
      .filter(Boolean);
  }

  if (!Array.isArray(daysActive)) {
    daysActive = [];
  }

  const actualPrice = safeNumber(
    source.actual_price || source.actualPrice,
    0
  );

  const offerPrice = safeNumber(
    source.combo_price ||
      source.offer_price ||
      source.offerPrice ||
      source.special_price ||
      source.addon_special_price,
    0
  );

  const customerSave = safeNumber(
    source.customer_save ||
      source.customerSave ||
      (actualPrice > 0 && offerPrice > 0 ? actualPrice - offerPrice : 0),
    0
  );

  return {
    ...source,
    id: source.id || source.offer_id || source.offerId,
    offer_type: offerType,
    offer_name:
      source.offer_name ||
      source.offerName ||
      source.combo_name ||
      source.comboName ||
      source.offer_text ||
      source.offerText ||
      "Special Offer",
    offer_text:
      source.offer_text ||
      source.offerText ||
      source.description ||
      source.offer_name ||
      "Special offer available",
    combo_items: comboItems,
    progressive_steps: progressiveSteps,
    days_active: daysActive,
    actual_price: actualPrice,
    offer_price: offerPrice,
    customer_save: Math.max(0, customerSave),
    is_active:
      source.is_active === undefined
        ? true
        : [true, 1, "1", "true", "active"].includes(source.is_active),
  };
};

const normalizeVendor = (rawVendor = {}, fallbackIndex = 0) => {
  const vendorInfo =
    rawVendor.vendor ||
    rawVendor.shop ||
    rawVendor.restaurant ||
    rawVendor.business ||
    {};

  const source = {
    ...vendorInfo,
    ...rawVendor,
  };

  let rawOffers =
    source.offers ||
    source.activeOffers ||
    source.active_offers ||
    source.offerList ||
    source.offer_list ||
    [];

  rawOffers = safeJsonParse(rawOffers, rawOffers);

  if (!Array.isArray(rawOffers)) {
    rawOffers = [];
  }

  const offers = rawOffers
    .map(normalizeOffer)
    .filter((offer) => offer.is_active !== false);

  const distanceKm = safeNumber(
    source.distanceKm ||
      source.distance_km ||
      source.distance ||
      source.shopDistance,
    0
  );

  const vendorId =
    source.vendorId ||
    source.vendor_id ||
    source.userId ||
    source.user_id ||
    source.id;

  const statusText =
    source.status ||
    source.shopStatus ||
    source.shop_status ||
    (source.isOpen || source.is_open ? "Open Now" : "Closed");

  return {
    ...source,
    id: vendorId || `vendor-${fallbackIndex}`,
    vendorId,
    vendorType:
      source.vendorType ||
      source.vendor_type ||
      source.businessType ||
      source.business_type ||
      "fastfood",
    name:
      source.shopName ||
      source.shop_name ||
      source.businessName ||
      source.business_name ||
      source.name ||
      "Food Shop",
    category:
      source.category ||
      source.categories ||
      source.shopCategory ||
      source.shop_category ||
      "Fast Food",
    distanceKm,
    distanceLabel:
      source.distanceLabel ||
      source.distance_label ||
      (distanceKm > 0 ? `${distanceKm.toFixed(1)} KM` : "Nearby"),
    status: statusText,
    isOpen:
      source.isOpen === true ||
      source.is_open === true ||
      String(statusText).toLowerCase().includes("open"),
    deliveryAvailable:
      source.deliveryAvailable === true ||
      source.delivery_available === true ||
      source.enable_delivery === 1 ||
      source.enable_delivery === true,
    pureVeg:
      source.pureVeg === true ||
      source.pure_veg === true ||
      source.isPureVeg === true ||
      source.is_pure_veg === true ||
      String(source.badgeType || source.badge_type || "")
        .toLowerCase()
        .includes("veg"),
    offers,
  };
};

const flattenOffersIntoVendors = (rawOffers = []) => {
  const vendorMap = new Map();

  rawOffers.forEach((rawOffer, index) => {
    const normalizedOffer = normalizeOffer(rawOffer);

    const vendorId =
      rawOffer.vendorId ||
      rawOffer.vendor_id ||
      rawOffer.userId ||
      rawOffer.user_id ||
      rawOffer.vendor?.id ||
      rawOffer.vendor?.vendor_id;

    const vendorKey = String(vendorId || `unknown-${index}`);

    if (!vendorMap.has(vendorKey)) {
      vendorMap.set(
        vendorKey,
        normalizeVendor(
          {
            ...(rawOffer.vendor || {}),
            vendorId,
            vendor_id: vendorId,
            vendorType:
              rawOffer.vendorType ||
              rawOffer.vendor_type ||
              rawOffer.businessType ||
              "fastfood",
            name:
              rawOffer.vendorName ||
              rawOffer.vendor_name ||
              rawOffer.shopName ||
              rawOffer.shop_name ||
              rawOffer.vendor?.name ||
              rawOffer.vendor?.shopName ||
              "Food Shop",
            shopImage:
              rawOffer.shopImage ||
              rawOffer.shop_image ||
              rawOffer.vendor?.shopImage ||
              rawOffer.vendor?.shop_image,
            distanceKm:
              rawOffer.distanceKm ||
              rawOffer.distance_km ||
              rawOffer.vendor?.distanceKm ||
              rawOffer.vendor?.distance_km,
            status:
              rawOffer.vendorStatus ||
              rawOffer.vendor_status ||
              rawOffer.vendor?.status ||
              "Open Now",
            offers: [],
          },
          index
        )
      );
    }

    vendorMap.get(vendorKey).offers.push(normalizedOffer);
  });

  return Array.from(vendorMap.values());
};

const extractVendorsFromResponse = (data) => {
  if (!data) {
    return [];
  }

  if (Array.isArray(data)) {
    const containsVendorGroups = data.some(
      (item) =>
        Array.isArray(item?.offers) ||
        Array.isArray(item?.activeOffers) ||
        Array.isArray(item?.active_offers)
    );

    return containsVendorGroups
      ? data.map(normalizeVendor)
      : flattenOffersIntoVendors(data);
  }

  const groupedVendors =
    data.vendors ||
    data.restaurants ||
    data.shops ||
    data.data?.vendors ||
    data.data?.restaurants ||
    data.data?.shops;

  if (Array.isArray(groupedVendors)) {
    return groupedVendors.map(normalizeVendor);
  }

  const flatOffers =
    data.offers ||
    data.data?.offers ||
    data.activeOffers ||
    data.active_offers;

  if (Array.isArray(flatOffers)) {
    return flattenOffersIntoVendors(flatOffers);
  }

  return [];
};

const getOfferSearchText = (offer) => {
  const comboNames = (offer.combo_items || [])
    .map((item) => item.name || item.product_name || item.productName)
    .filter(Boolean)
    .join(" ");

  const progressiveText = (offer.progressive_steps || [])
    .map(
      (step) =>
        `${step.title || step.label || ""} ${
          step.reward_product_name || step.productName || ""
        }`
    )
    .join(" ");

  return [
    offer.offer_name,
    offer.offer_text,
    offer.offer_type,
    offer.combo_name,
    offer.addon_product_name,
    offer.buy_product_name,
    offer.get_product_name,
    comboNames,
    progressiveText,
  ]
    .join(" ")
    .toLowerCase();
};

const getOfferEndMinutes = (offer) => {
  if (!offer.end_time) {
    return Number.MAX_SAFE_INTEGER;
  }

  const match = String(offer.end_time).match(/^(\d{1,2}):(\d{2})/);

  if (!match) {
    return Number.MAX_SAFE_INTEGER;
  }

  return Number(match[1]) * 60 + Number(match[2]);
};

const getCurrentMinutes = () => {
  const now = new Date();
  return now.getHours() * 60 + now.getMinutes();
};

const getOfferTimingStatus = (offer) => {
  if (normalizeOfferType(offer.offer_type) !== "time_slot") {
    return null;
  }

  const startMatch = String(offer.start_time || "").match(
    /^(\d{1,2}):(\d{2})/
  );
  const endMatch = String(offer.end_time || "").match(/^(\d{1,2}):(\d{2})/);

  if (!startMatch || !endMatch) {
    return null;
  }

  const startMinutes = Number(startMatch[1]) * 60 + Number(startMatch[2]);
  const endMinutes = Number(endMatch[1]) * 60 + Number(endMatch[2]);
  const currentMinutes = getCurrentMinutes();

  let isLive = false;

  if (endMinutes >= startMinutes) {
    isLive =
      currentMinutes >= startMinutes && currentMinutes <= endMinutes;
  } else {
    isLive =
      currentMinutes >= startMinutes || currentMinutes <= endMinutes;
  }

  return {
    isLive,
    label: isLive ? "LIVE NOW" : `Starts ${formatTime(offer.start_time)}`,
  };
};

function OfferDetails({ offer }) {
  const type = normalizeOfferType(offer.offer_type);

  if (type === "combo") {
    const itemNames = (offer.combo_items || [])
      .map((item) => {
        const quantity = safeNumber(item.quantity || item.qty, 1);
        const name =
          item.name ||
          item.product_name ||
          item.productName ||
          "Item";

        return quantity > 1 ? `${name} × ${quantity}` : name;
      })
      .filter(Boolean);

    return (
      <>
        {itemNames.length > 0 && (
          <p className="fro-offer-description">
            {itemNames.join(" + ")}
          </p>
        )}

        <div className="fro-price-row">
          {offer.actual_price > 0 && (
            <span className="fro-old-price">
              {formatMoney(offer.actual_price)}
            </span>
          )}

          {offer.offer_price > 0 && (
            <strong>{formatMoney(offer.offer_price)}</strong>
          )}

          {offer.customer_save > 0 && (
            <span className="fro-save-label">
              Save {formatMoney(offer.customer_save)}
            </span>
          )}
        </div>
      </>
    );
  }

  if (type === "bill_discount") {
    const discountValue = safeNumber(offer.discount_value, 0);
    const discountType = String(
      offer.discount_type || "flat"
    ).toLowerCase();

    return (
      <>
        <p className="fro-offer-description">
          {discountType === "percent"
            ? `${discountValue}% discount`
            : `${formatMoney(discountValue)} discount`}
          {safeNumber(offer.min_bill_amount || offer.min_purchasing_price, 0) >
          0
            ? ` on minimum bill ${formatMoney(
                offer.min_bill_amount || offer.min_purchasing_price
              )}`
            : ""}
        </p>

        {safeNumber(offer.max_discount, 0) > 0 && (
          <p className="fro-offer-rule">
            Maximum discount {formatMoney(offer.max_discount)}
          </p>
        )}
      </>
    );
  }

  if (type === "bill_unlock_addon") {
    return (
      <>
        <p className="fro-offer-description">
          Spend{" "}
          {formatMoney(
            offer.min_bill_amount || offer.min_purchasing_price
          )}{" "}
          and get {offer.addon_product_name || "special item"} at{" "}
          <strong>{formatMoney(offer.addon_special_price)}</strong>
        </p>

        {safeNumber(offer.addon_original_price, 0) > 0 && (
          <p className="fro-offer-rule">
            Regular price{" "}
            <span className="fro-old-price">
              {formatMoney(offer.addon_original_price)}
            </span>
          </p>
        )}
      </>
    );
  }

  if (type === "buy_x_get_y") {
    const rewardType = String(offer.reward_type || "free").toLowerCase();

    let rewardLabel = "Free";

    if (rewardType === "flat_price") {
      rewardLabel = `at ${formatMoney(offer.reward_value)}`;
    }

    if (rewardType === "percent_discount") {
      rewardLabel = `${safeNumber(offer.reward_value, 0)}% OFF`;
    }

    return (
      <p className="fro-offer-description">
        Buy {safeNumber(offer.buy_quantity, 1)}{" "}
        {offer.buy_product_name || "item"} and get{" "}
        {safeNumber(offer.get_quantity, 1)}{" "}
        {offer.get_product_name || "reward item"} {rewardLabel}
      </p>
    );
  }

  if (type === "time_slot") {
    const timingStatus = getOfferTimingStatus(offer);

    return (
      <>
        <p className="fro-offer-description">
          {offer.discount_type === "percent"
            ? `${safeNumber(offer.discount_value, 0)}% OFF`
            : `${formatMoney(offer.discount_value)} OFF`}
          {safeNumber(offer.min_bill_amount, 0) > 0
            ? ` on bill ${formatMoney(offer.min_bill_amount)}+`
            : ""}
        </p>

        <div className="fro-time-row">
          <span>
            {formatTime(offer.start_time)} –{" "}
            {formatTime(offer.end_time)}
          </span>

          {timingStatus && (
            <strong className={timingStatus.isLive ? "live" : ""}>
              {timingStatus.label}
            </strong>
          )}
        </div>
      </>
    );
  }

  if (type === "progressive_unlock") {
    return (
      <div className="fro-progressive-list">
        {(offer.progressive_steps || []).map((step, index) => {
          const rewardType = String(
            step.reward_type || "flat_price"
          ).toLowerCase();

          let rewardText = step.title || step.reward_label || "Reward";

          if (step.reward_product_name) {
            rewardText = step.reward_product_name;
          }

          if (rewardType === "percent_discount") {
            rewardText = `${safeNumber(step.reward_value, 0)}% OFF`;
          } else if (rewardType === "flat_price") {
            rewardText = `${rewardText} at ${formatMoney(
              step.reward_value
            )}`;
          } else if (rewardType === "free") {
            rewardText = `${rewardText} Free`;
          }

          return (
            <div
              className="fro-progressive-step"
              key={`${offer.id}-step-${index}`}
            >
              <span>{index + 1}</span>
              <p>
                Spend {formatMoney(step.min_bill_amount)} →{" "}
                <strong>{rewardText}</strong>
              </p>
            </div>
          );
        })}
      </div>
    );
  }

  if (type === "coin_reward") {
    const coinJson =
      safeJsonParse(offer.offer_json, {}) || offer;

    const earnPercent = safeNumber(
      offer.coin_earn_percent ||
        coinJson.coin_earn_percent ||
        offer.reward_value,
      0
    );

    const minBill = safeNumber(
      offer.coin_min_bill_amount ||
        coinJson.coin_min_bill_amount ||
        offer.min_bill_amount ||
        offer.min_purchasing_price,
      0
    );

    const maxEarn = safeNumber(
      offer.coin_max_earn_per_order ||
        coinJson.coin_max_earn_per_order,
      0
    );

    const coinValue = safeNumber(
      offer.coin_value_rupees ||
        coinJson.coin_value_rupees,
      1
    );

    const expiryDays = safeNumber(
      offer.coin_expiry_days ||
        coinJson.coin_expiry_days,
      0
    );

    return (
      <>
        <p className="fro-offer-description">
          Earn <strong>{earnPercent}% Shop Coins</strong>
          {minBill > 0
            ? ` on minimum bill ${formatMoney(minBill)}`
            : ""}
        </p>

        <div className="fro-coin-rules">
          {maxEarn > 0 && <span>Max {maxEarn} coins/order</span>}
          <span>1 coin = {formatMoney(coinValue)}</span>
          {expiryDays > 0 && <span>Valid for {expiryDays} days</span>}
        </div>
      </>
    );
  }

  return (
    <p className="fro-offer-description">
      {offer.offer_text || "Special offer available"}
    </p>
  );
}

function FoodRadarOffers() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [vendors, setVendors] = useState([]);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [sortBy, setSortBy] = useState("nearest");

  const [location, setLocation] = useState(null);
  const [locationStatus, setLocationStatus] = useState(
    "Detecting location..."
  );

  const [loading, setLoading] = useState(true);
  const [locationLoading, setLocationLoading] = useState(true);
  const [error, setError] = useState("");

  const locationRequestRef = useRef(false);

  const queryVendorId = searchParams.get("vendorId");
  const queryLatitude = searchParams.get("lat");
  const queryLongitude = searchParams.get("lng");

  const loadOffers = async (selectedLocation) => {
    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams();

      if (selectedLocation?.latitude) {
        params.set("lat", selectedLocation.latitude);
      }

      if (selectedLocation?.longitude) {
        params.set("lng", selectedLocation.longitude);
      }

      if (queryVendorId) {
        params.set("vendorId", queryVendorId);
      }

      params.set("vendorType", "fastfood");

     const response = await fetch(
      `/api/public/food-radar/offers?${params.toString()}`
     );

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success === false) {
        throw new Error(
          data.error ||
            data.message ||
            "Offers load nahi ho paaye."
        );
      }

      const normalizedVendors = extractVendorsFromResponse(data)
        .map((vendor, index) => normalizeVendor(vendor, index))
        .filter((vendor) => vendor.offers.length > 0);

      setVendors(normalizedVendors);
    } catch (loadError) {
      console.error("Food Radar offers load error:", loadError);
      setVendors([]);
      setError(
        loadError.message ||
          "Offers load nahi ho paaye. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const requestLocation = () => {
    if (locationRequestRef.current) {
      return;
    }

    if (!navigator.geolocation) {
      setLocationLoading(false);
      setLocationStatus("Location not supported");
      setError(
        "Aapke browser me location support available nahi hai."
      );
      return;
    }

    locationRequestRef.current = true;
    setLocationLoading(true);
    setError("");

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        locationRequestRef.current = false;

        const detectedLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };

        setLocation(detectedLocation);
        setLocationStatus("Current Location");
        setLocationLoading(false);

        await loadOffers(detectedLocation);
      },
      (locationError) => {
        locationRequestRef.current = false;
        setLocationLoading(false);
        setLoading(false);
        setLocation(null);
        setLocationStatus("Location Required");

        if (locationError.code === 1) {
          setError(
            "Nearby offers dikhane ke liye location permission allow karein."
          );
        } else {
          setError(
            "Current location detect nahi ho paayi. Dobara try karein."
          );
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 30000,
      }
    );
  };

  useEffect(() => {
    const lat = safeNumber(queryLatitude, NaN);
    const lng = safeNumber(queryLongitude, NaN);

    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      const queryLocation = {
        latitude: lat,
        longitude: lng,
      };

      setLocation(queryLocation);
      setLocationStatus("Current Location");
      setLocationLoading(false);
      loadOffers(queryLocation);
      return;
    }

    requestLocation();
  }, [queryLatitude, queryLongitude, queryVendorId]);

  const filteredVendors = useMemo(() => {
    const searchValue = search.trim().toLowerCase();

    let result = vendors
      .map((vendor) => {
        const vendorSearchMatch =
          !searchValue ||
          String(vendor.name || "")
            .toLowerCase()
            .includes(searchValue) ||
          String(vendor.category || "")
            .toLowerCase()
            .includes(searchValue);

        const matchingOffers = vendor.offers.filter((offer) => {
          const matchesType =
            activeFilter === "all" ||
            normalizeOfferType(offer.offer_type) === activeFilter;

          const matchesSearch =
            !searchValue ||
            vendorSearchMatch ||
            getOfferSearchText(offer).includes(searchValue);

          return matchesType && matchesSearch;
        });

        return {
          ...vendor,
          offers: matchingOffers,
        };
      })
      .filter((vendor) => vendor.offers.length > 0);

    if (sortBy === "nearest") {
      result.sort(
        (first, second) =>
          safeNumber(first.distanceKm, 99999) -
          safeNumber(second.distanceKm, 99999)
      );
    }

    if (sortBy === "most_offers") {
      result.sort(
        (first, second) =>
          second.offers.length - first.offers.length
      );
    }

    if (sortBy === "highest_saving") {
      result.sort((first, second) => {
        const firstSaving = Math.max(
          0,
          ...first.offers.map((offer) =>
            safeNumber(offer.customer_save, 0)
          )
        );

        const secondSaving = Math.max(
          0,
          ...second.offers.map((offer) =>
            safeNumber(offer.customer_save, 0)
          )
        );

        return secondSaving - firstSaving;
      });
    }

    if (sortBy === "ending_soon") {
      result.sort((first, second) => {
        const firstEndTime = Math.min(
          ...first.offers.map(getOfferEndMinutes)
        );

        const secondEndTime = Math.min(
          ...second.offers.map(getOfferEndMinutes)
        );

        return firstEndTime - secondEndTime;
      });
    }

    return result;
  }, [vendors, search, activeFilter, sortBy]);

  const totalOffers = useMemo(() => {
    return filteredVendors.reduce(
      (total, vendor) => total + vendor.offers.length,
      0
    );
  }, [filteredVendors]);

  const handleOrderNow = (vendor, offer = null) => {
    const vendorId =
      vendor.vendorId || vendor.vendor_id || vendor.id;

    if (!vendorId) {
      window.alert("Vendor ID missing hai.");
      return;
    }

    const params = new URLSearchParams();

    if (offer?.id) {
      params.set("offerId", offer.id);
    }

    if (location?.latitude) {
      params.set("lat", location.latitude);
    }

    if (location?.longitude) {
      params.set("lng", location.longitude);
    }

    const query = params.toString();

    navigate(
      `/food-radar/order/${vendorId}${query ? `?${query}` : ""}`
    );
  };

  const handleVendorOffers = (vendor) => {
    const vendorId =
      vendor.vendorId || vendor.vendor_id || vendor.id;

    if (!vendorId) {
      return;
    }

    const params = new URLSearchParams();

    params.set("vendorId", vendorId);

    if (location?.latitude) {
      params.set("lat", location.latitude);
    }

    if (location?.longitude) {
      params.set("lng", location.longitude);
    }

    navigate(`/food-radar/offers?${params.toString()}`);
  };

  const handleRefresh = () => {
    if (location) {
      loadOffers(location);
    } else {
      requestLocation();
    }
  };

  const handleHome = () => {
    navigate("/food-radar");
  };

  return (
    <div className="fro-page">
      <header className="fro-header">
        <button
          type="button"
          className="fro-back-button"
          onClick={handleHome}
          aria-label="Go back"
        >
          ←
        </button>

        <div className="fro-header-title">
          <p>VAKRA Food Radar</p>
          <h1>Offers Near You</h1>
        </div>

        <button
          type="button"
          className="fro-refresh-button"
          onClick={handleRefresh}
          disabled={loading || locationLoading}
          aria-label="Refresh offers"
        >
          ↻
        </button>
      </header>

      <main className="fro-main">
        <section className="fro-location-card">
          <div className="fro-location-icon">📍</div>

          <div className="fro-location-content">
            <p>Your Location</p>
            <h2>{locationStatus}</h2>
            <span>
              Nearby shops ke active offers yahan dikh rahe hain.
            </span>
          </div>

          <button
            type="button"
            onClick={requestLocation}
            disabled={locationLoading}
          >
            {locationLoading ? "Loading..." : "Update"}
          </button>
        </section>

        <section className="fro-hero">
          <div>
            <p>🔥 Live Food Deals</p>
            <h2>
              Nearby shops ke saare offers ek jagah
            </h2>
            <span>
              Combos, discounts, happy hours aur Shop Coins.
            </span>
          </div>

          <div className="fro-hero-icon">🎁</div>
        </section>

        <section className="fro-search-section">
          <div className="fro-search-box">
            <span>🔎</span>

            <input
              type="text"
              placeholder="Search shop, food or offer..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />

            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          <select
            className="fro-sort-select"
            value={sortBy}
            onChange={(event) => setSortBy(event.target.value)}
          >
            {SORT_OPTIONS.map((option) => (
              <option
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </select>
        </section>

        <section className="fro-filter-row">
          {OFFER_FILTERS.map((filter) => (
            <button
              type="button"
              key={filter.key}
              className={
                activeFilter === filter.key ? "active" : ""
              }
              onClick={() => setActiveFilter(filter.key)}
            >
              <span>{filter.icon}</span>
              {filter.label}
            </button>
          ))}
        </section>

        <section className="fro-summary-row">
          <div>
            <strong>{filteredVendors.length}</strong>
            <span>Shops</span>
          </div>

          <div>
            <strong>{totalOffers}</strong>
            <span>Live Offers</span>
          </div>

          <div>
            <strong>
              {
                filteredVendors.filter(
                  (vendor) => vendor.isOpen
                ).length
              }
            </strong>
            <span>Open Now</span>
          </div>
        </section>

        {error && (
          <section className="fro-error-card">
            <span>⚠️</span>

            <div>
              <h3>Offers load nahi hue</h3>
              <p>{error}</p>
            </div>

            <button type="button" onClick={handleRefresh}>
              Try Again
            </button>
          </section>
        )}

        {loading ? (
          <section className="fro-loading-state">
            <div className="fro-spinner" />
            <h3>Nearby offers load ho rahe hain...</h3>
            <p>Shops aur unke active deals check kiye ja rahe hain.</p>
          </section>
        ) : !error && filteredVendors.length === 0 ? (
          <section className="fro-empty-state">
            <div>🎁</div>
            <h3>Koi offer nahi mila</h3>
            <p>
              Current filter ya location par abhi koi active offer
              available nahi hai.
            </p>

            <button
              type="button"
              onClick={() => {
                setSearch("");
                setActiveFilter("all");
              }}
            >
              Show All Offers
            </button>
          </section>
        ) : (
          <section className="fro-vendor-list">
            {filteredVendors.map((vendor) => {
              const imageSrc = getVendorImageSrc(vendor);

              const maximumSaving = Math.max(
                0,
                ...vendor.offers.map((offer) =>
                  safeNumber(offer.customer_save, 0)
                )
              );

              return (
                <article
                  className="fro-vendor-card"
                  key={vendor.id}
                >
                  <div className="fro-vendor-header">
                    <div className="fro-vendor-image">
                      {imageSrc ? (
                        <img
                          src={imageSrc}
                          alt={vendor.name}
                          loading="lazy"
                          onError={(event) => {
                            event.currentTarget.style.display =
                              "none";

                            const fallback =
                              event.currentTarget.parentElement.querySelector(
                                ".fro-vendor-image-fallback"
                              );

                            if (fallback) {
                              fallback.style.display = "grid";
                            }
                          }}
                        />
                      ) : null}

                      <span
                        className="fro-vendor-image-fallback"
                        style={{
                          display: imageSrc ? "none" : "grid",
                        }}
                      >
                        🍽️
                      </span>
                    </div>

                    <div className="fro-vendor-details">
                      <div className="fro-vendor-name-row">
                        <div>
                          <h2>{vendor.name}</h2>
                          <p>{vendor.category}</p>
                        </div>

                        {vendor.pureVeg && (
                          <span className="fro-veg-badge">
                            PURE VEG
                          </span>
                        )}
                      </div>

                      <div className="fro-vendor-meta">
                        <span>📍 {vendor.distanceLabel}</span>

                        <span
                          className={
                            vendor.isOpen
                              ? "fro-open-status"
                              : "fro-closed-status"
                          }
                        >
                          ● {vendor.status}
                        </span>

                        {vendor.deliveryAvailable && (
                          <span>🛵 Delivery</span>
                        )}
                      </div>

                      <div className="fro-vendor-offer-summary">
                        <strong>
                          {vendor.offers.length}{" "}
                          {vendor.offers.length === 1
                            ? "Offer"
                            : "Offers"}
                        </strong>

                        {maximumSaving > 0 && (
                          <span>
                            Save up to{" "}
                            {formatMoney(maximumSaving)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="fro-offer-list">
                    {vendor.offers.map((offer) => {
                      const config = getOfferTypeConfig(
                        offer.offer_type
                      );

                      return (
                        <article
                          className={`fro-offer-card ${config.className}`}
                          key={
                            offer.id ||
                            `${vendor.id}-${offer.offer_name}`
                          }
                        >
                          <div className="fro-offer-card-head">
                            <div
                              className={`fro-offer-type-icon ${config.className}`}
                            >
                              {config.icon}
                            </div>

                            <div className="fro-offer-title">
                              <span>{config.label}</span>
                              <h3>{offer.offer_name}</h3>
                            </div>

                            {offer.customer_save > 0 && (
                              <div className="fro-saving-badge">
                                Save{" "}
                                {formatMoney(
                                  offer.customer_save
                                )}
                              </div>
                            )}
                          </div>

                          <OfferDetails offer={offer} />

                          {offer.offer_text &&
                            offer.offer_text !==
                              offer.offer_name && (
                              <p className="fro-offer-note">
                                {offer.offer_text}
                              </p>
                            )}

                          <div className="fro-offer-actions">
                            <button
                              type="button"
                              className="fro-secondary-action"
                              onClick={() =>
                                handleVendorOffers(vendor)
                              }
                            >
                              Shop Offers
                            </button>

                            <button
                              type="button"
                              className="fro-primary-action"
                              onClick={() =>
                                handleOrderNow(vendor, offer)
                              }
                            >
                              Order Now
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    className="fro-view-menu-button"
                    onClick={() => handleOrderNow(vendor)}
                  >
                    View Full Menu
                    <span>→</span>
                  </button>
                </article>
              );
            })}
          </section>
        )}
      </main>

      <nav className="fro-bottom-nav">
        <button type="button" onClick={handleHome}>
          <span>🏠</span>
          Home
        </button>

        <button type="button" className="active">
          <span>🎁</span>
          Offers
        </button>

        <button
          type="button"
          onClick={() => navigate("/food-radar")}
        >
          <span>🔎</span>
          Search
        </button>

        <button
          type="button"
          onClick={() => navigate("/food-radar/orders")}
        >
          <span>📦</span>
          Orders
        </button>

        <button
          type="button"
          onClick={() => navigate("/food-radar/profile")}
        >
          <span>👤</span>
          Profile
        </button>
      </nav>
    </div>
  );
}

export default FoodRadarOffers;
